import React, { useState } from 'react';
import { M } from '@dashboard-experience/mastodon';
import { Translate as T } from 'react-redux-i18n';
import { Field } from 'redux-form';
import { i18n } from '@international/mastodon-i18n';
import { DropDown } from '../../fields';
import { ModalFooter, ModalHeader } from './DocumentVerificationComponents';
import {
  getDocumentOptions,
  updateStateArray,
  isFourDigitYear,
} from './utils/formatter';
import { DateObject, DocumentInfoModalProps } from './utils/interfaces';
import {
  getSelectYearOption,
  selectYear,
  multiDocuments,
} from './utils/constants';

import './DocumentInfoModal.scss';

const DocumentInfoModal: React.FC<DocumentInfoModalProps> = ({
  file,
  documentsRequired,
  onDocumentsRequiredChange,
  handleUploadedFilesUpdate,
  onCancel,
  onSubmit,
  documentTypes,
  uploadedFiles,
}) => {
  const exceptionYears: DateObject[] = [
    { value: '', name: '', uploadId: '', disabled: true },
    ...(documentsRequired.some(doc => doc.value === selectYear)
      ? []
      : getSelectYearOption()),
    ...documentsRequired,
  ];

  const [state, setState] = useState({
    years: exceptionYears,
    selectedYears: Array(file.length).fill(''),
    selectedDocumentTypes: Array(file.length).fill(''),
    hasMissingDocuments: false,
  });

  const handleYearChange = (
    selectedYearValue: DateObject,
    index: number,
    uploadId: string,
  ) => {
    const matchIndex = state.years.findIndex(
      year =>
        year.value === selectedYearValue.value &&
        year.value !== selectYear &&
        !year.disabled,
    );

    const updatedYears = state.years.map((year, i) => {
      if (year.uploadId === uploadId) {
        return {
          ...year,
          disabled: false,
          type: '',
          uploadId: '',
          requiredForVerification: true,
        };
      }
      if (i === matchIndex) {
        return {
          ...year,
          disabled: true,
          type: state.selectedDocumentTypes[index].value,
          requiredForVerification:
            state.selectedDocumentTypes[index].requiredForVerification,
          uploadId,
          index: i,
        };
      }
      return year;
    });

    setState(prevState => ({
      ...prevState,
      years: updatedYears,
      hasMissingDocuments: false,
    }));
    updateStateArray(setState, 'selectedYears', index, selectedYearValue.value);
    onDocumentsRequiredChange(updatedYears.slice(1));
  };

  const handleDocumentTypeChange = (
    selectedTypeItem: DateObject,
    index: number,
    uploadId: string,
  ) => {
    const exceptionId = state.years.find(
      item => item?.exceptionType === multiDocuments,
    )?.id;

    const updatedYears = state.years.map((year: any) => {
      const isTargetYear =
        year.uploadId === uploadId && year.requiredForVerification;
      const isSelectedYear =
        year.value === state.selectedYears[index] &&
        year.requiredForVerification &&
        year.value !== selectYear;

      if (isTargetYear || isSelectedYear) {
        if (selectedTypeItem.requiredForVerification) {
          return {
            ...year,
            type: selectedTypeItem.value,
            requiredForVerification: selectedTypeItem.requiredForVerification,
            uploadId,
          };
        }
        return {
          ...year,
          type: '',
          uploadId: '',
          disabled: false,
          requiredForVerification: true,
        };
      }

      return year;
    });

    if (!selectedTypeItem.requiredForVerification) {
      updateStateArray(setState, 'selectedYears', index, '');
      updatedYears.push({
        type: selectedTypeItem.value,
        requiredForVerification: selectedTypeItem.requiredForVerification,
        uploadId,
        id: exceptionId,
        exceptionType: multiDocuments,
      });
    }

    setState(prevState => ({
      ...prevState,
      years: updatedYears,
      hasMissingDocuments: false,
    }));
    updateStateArray(setState, 'selectedDocumentTypes', index, {
      value: selectedTypeItem.value,
      requiredForVerification: selectedTypeItem.requiredForVerification,
    });
    onDocumentsRequiredChange(updatedYears.slice(1));
  };

  const allFieldsFilled = state.selectedYears.every((year, index) => {
    const type = state.selectedDocumentTypes[index];

    if (type.requiredForVerification === false) return true;
    return isFourDigitYear(year) && type !== '';
  });

  const handleSubmit = () => {
    if (allFieldsFilled) {
      onSubmit();
      handleUploadedFilesUpdate(state.years, uploadedFiles);
    } else {
      setState(prevState => ({
        ...prevState,
        hasMissingDocuments: true,
      }));
    }
  };

  const getDropdownClass = (hasError: boolean) =>
    hasError ? 'dropdown-wrapper error' : 'dropdown-wrapper';

  const renderDropdowns = (doc: any, i: number) => {
    const docTypeError =
      state.hasMissingDocuments && state.selectedDocumentTypes[i] === '';

    const yearError =
      state.hasMissingDocuments &&
      (state.selectedYears[i] === '' || state.selectedYears[i] === selectYear);

    return (
      <div className='dropdown-section'>
        <div className={getDropdownClass(docTypeError)}>
          <Field
            type='select'
            name='documentType'
            component={DropDown}
            label={i18n.getStr('labels.documentType')}
            input={{
              onChange: (e: any) =>
                handleDocumentTypeChange(e.selectedItem, i, doc.uploadId),
            }}
            options={getDocumentOptions(documentTypes)}
            showDobError={false}
          />
        </div>

        {state.selectedDocumentTypes[i].requiredForVerification !== false && (
          <div className={getDropdownClass(yearError)}>
            <Field
              type='select'
              name='documentYear'
              component={DropDown}
              label={i18n.getStr('labels.documentYear')}
              input={{
                onChange: (e: any) =>
                  handleYearChange(e.selectedItem, i, doc.uploadId),
              }}
              options={state.years.filter(item => item.value !== undefined)}
              showDobError={false}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='modal-overlay'>
      <div className='modal-content'>
        <ModalHeader
          onCancel={onCancel}
          textContent={i18n.getStr('components.employment.uploadSuccessful')}
        />
        <div className='modal-body'>
          <b>
            <T
              value='components.Verification.EmploymentMultidocPanel.documentModal'
              className='text-panel text-bold'
              dangerousHTML
            />
          </b>
          {file.map((doc: any, i) => (
            <React.Fragment key={doc.uploadId}>
              <div className='success-message'>
                <div data-testid='success-icon' className='success-icon'>
                  <M.Icon icon='CheckmarkFilled' />
                </div>
                <p className='text-panel'>{doc.filename}</p>
              </div>
              {renderDropdowns(doc, i)}
            </React.Fragment>
          ))}
          {state.hasMissingDocuments && (
            <p className='font-weight-light'>
              <T
                value='components.employment.missingModalDocuments'
                className='text-danger'
              />
            </p>
          )}
        </div>
        <ModalFooter
          onCancel={onCancel}
          handleSubmit={handleSubmit}
          button='buttons.confirm'
        />
      </div>
    </div>
  );
};
export default DocumentInfoModal;
